import { resolveOrganizationFilter } from '@/lib/dashboard-filter.mjs';
import PressureCard from "@/components/PressureCard";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { STATUS, statusLabel, fmtRelative } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Search, MapPin, Activity, AlertTriangle, Wifi, WifiOff, ChevronRight, History, Radio, ShieldCheck, Monitor, Play, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useRefresh } from "@/context/RefreshContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ValueGuideButton, InfoHint } from "@/components/InfoHint";
import { canOperateDevice, canMeasure, isAdmin, isDemo } from "@/lib/permissions";
import { NextMeasurement } from "@/components/NextMeasurement";

const DEVICE_ORDER = { QC105: 0, "THRM-STAGING-REVPI-001": 1 };

function SummaryItem({ label, value, accent }) {
  return (
    <div className="min-w-0 bg-card px-4 py-3.5 sm:px-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold tracking-tight ${accent || ""}`}>{value}</div>
    </div>
  );
}

function isoTriplet(metrics) {
  const keys = ["ISO_4um", "ISO_6um", "ISO_14um"];
  const values = keys.map((key) => metrics?.find((metric) => metric.key === key)?.value);
  if (values.every((value) => value === null || value === undefined)) return "—/—/—";
  return values.map((value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    const number = Number(value);
    return Number.isInteger(number) ? String(number) : number.toFixed(1);
  }).join("/");
}

function DeviceCard({ device, onClick, user, onMeasured }) {
  const { t, lang } = useLang();
  const [measureBusy, setMeasureBusy] = useState(false);

  const cleanliness = STATUS[device.status_color] || STATUS.grey;
  const statusColor = device.status_badge_color || device.status_color;
  const status = STATUS[statusColor] || STATUS.grey;
  const measuredIso = isoTriplet(device.summary_metrics || []);
  const start = device.profile?.start;
  const limitIso = start ? `${start.iso4}/${start.iso6}/${start.iso14}` : "—/—/—";
  const filterSummary = device.filter_summary || {};
  const installationPending = device.installation && !["completed", "skipped"].includes(device.installation.status);
  const measureNow = async (event) => {
    event.stopPropagation();
    setMeasureBusy(true);
    try {
      await api.post(`/devices/${device.id}/commands`, { action: "MEASURE_NOW", parameters: {} });
      toast.success(t("Meetopdracht klaargezet.", "Measurement command queued."));
      onMeasured?.();
    } catch (error) { toast.error(error.response?.data?.detail || t("Meetopdracht mislukt.", "Measurement command failed.")); }
    finally { setMeasureBusy(false); }
  };
  const cleanlinessText = device.cleanliness_status === "clean" ? t("In orde", "Healthy") : device.cleanliness_status === "attention" ? t("Aandacht nodig", "Needs attention") : device.cleanliness_status === "inactive" ? t("Apparaat uitgeschakeld", "Device disabled") : t("Onvoldoende meetdata", "Insufficient data");
  const processText = ({ WAITING: t("Wachten op volgende cyclus", "Waiting for next cycle"), WARMUP: t("Sensoren opwarmen", "Warming up sensors"), MEASURING: t("Meten actief", "Measuring"), FILTERING: t("Filteren actief", "Filtering"), FAULT: t("Storing", "Fault"), MANUAL: t("Handmatige bediening", "Manual control") })[device.process_state] || (device.online ? t("Verbonden", "Connected") : t("Geen recente verbinding", "No recent connection"));
  return (
    <article
      onClick={onClick}
      onKeyDown={(event) => { if (event.key === "Enter") onClick(); }}
      role="link"
      tabIndex={0}
      data-testid={`device-card-${device.id}`}
      className={`group relative flex h-full min-h-[285px] flex-col overflow-hidden rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg ring-1 ring-transparent ${device.status_color === "red" ? "ring-red-500/20" : ""}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${cleanliness.dot}`} />

      <div className="flex items-start justify-between gap-4 pl-1">
        <div className="min-w-0 flex-1">
          <h2 className="break-words font-heading text-base font-black leading-tight tracking-tight">{device.name}</h2>
          <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="break-words">{device.location || t("Locatie niet ingesteld", "Location not set")}</span>
          </div>
        </div>
        <div className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${device.online ? "bg-emerald-500/10 text-emerald-600" : "bg-slate-500/10 text-slate-500"}`}>
          {device.online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          {device.online ? t("Online", "Online") : t("Offline", "Offline")}
        </div>
      </div>

      {installationPending && isAdmin(user) && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-amber-700 dark:text-amber-300" data-testid={`installation-warning-${device.id}`}>
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div><div className="text-[10px] font-black uppercase tracking-wide">{t("Installatie niet vrijgegeven", "Installation not released")}</div><div className="mt-0.5 text-[10px] opacity-85">{t(`Beheerder gaat verder bij stap ${device.installation.current_step} van 18.`, `Administrator continues at step ${device.installation.current_step} of 18.`)}</div></div>
        </div>
      )}

      <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3" data-testid={`card-metrics-${device.id}`}>
        <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
          <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">{device.online ? t("Gemeten", "Measured") : t("Laatst gemeten", "Last measured")}<InfoHint helpKey="ISO_4um" /></span>
          <span className="font-mono text-xl font-semibold tracking-tight text-foreground">ISO {measuredIso}</span>
          <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">{t("Grenswaarde", "Limit")}<InfoHint helpKey="profile_limit" /></span>
          <span className="font-mono text-sm font-semibold text-foreground">ISO {limitIso}</span>
          <span className="text-[11px] font-medium text-muted-foreground">{t("Reinheidsstatus", "Cleanliness")}</span>
          <span className={`flex items-center gap-1.5 text-xs font-bold ${cleanliness.text}`}><span className={`h-1.5 w-1.5 rounded-full ${cleanliness.dot}`} />{cleanlinessText}</span>
        </div>
      </div>

      <div className={`mt-3 flex min-h-[46px] items-center gap-2.5 rounded-lg px-3 py-2 ${status.bg} ${status.text}`} data-testid={`cleanliness-status-${device.id}`}>
        <span className={`h-2 w-2 shrink-0 rounded-full ${status.dot}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] opacity-75">{t("Processtatus", "Process status")}<InfoHint helpKey="process_status" /></div>
          <div className="break-words text-xs font-bold leading-tight">{processText}</div>
        </div>
      </div>

      {device.filter_warning && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-2.5 text-amber-700 dark:text-amber-400" data-testid={`filter-warning-${device.id}`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-wide">{t("Maximale filtertijd bereikt", "Maximum filtering time reached")}</div>
            <div className="mt-0.5 line-clamp-2 text-[10px] leading-4 opacity-90">{t("Het reinigingsdoel ISO 17/15/12 is niet bereikt. Controleer filter, doorstroming en olieconditie.", "The ISO 17/15/12 cleaning target was not reached. Check the filter, flow and oil condition.")}</div>
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`filter-summary-${device.id}`}>
        <div className="rounded-lg border border-border px-2.5 py-2">
          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-muted-foreground"><History className="h-3 w-3 shrink-0" />{t("Cycli · 30 dagen", "Cycles · 30 days")}</div>
          <div className="mt-0.5 font-mono text-lg font-semibold">{filterSummary.cycles_30d ?? 0}</div>
        </div>
        <div className="rounded-lg border border-border px-2.5 py-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{t("Laatste filtercyclus", "Last filter cycle")}</div>
          <div className="mt-0.5 text-xs font-semibold leading-tight">{filterSummary.last_cycle_at ? fmtRelative(filterSummary.last_cycle_at, lang) : t("Nog niet uitgevoerd", "Not run yet")}</div>
        </div>
      </div>

      <div className="mt-auto pt-3">
        <NextMeasurement device={device} compact className="mb-3 rounded-lg border border-border bg-muted/15 px-2.5 py-2" />
        {canMeasure(user) && canOperateDevice(user, device) && <Button size="sm" className="mb-2 w-full gap-2" onClick={measureNow} disabled={measureBusy} data-testid={`card-measure-${device.id}`}>{measureBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{measureBusy ? t("Opdracht klaarzetten…", "Queueing command…") : t("Nu meten", "Measure now")}</Button>}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border pt-2.5 text-[10px] text-muted-foreground">
          <span>{t("Laatste verbinding", "Last connection")}: {fmtRelative(device.last_seen, lang)}</span>
          <span className="flex items-center gap-1 font-semibold text-primary">{t("Bekijk details", "View details")}<ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </article>
  );
}

function TypedDeviceCard(props) { return props.device.device_type === "pressure_monitor" ? <PressureCard {...props} /> : <DeviceCard {...props} />; }

export default function Dashboard() {
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const { tick, refreshNow } = useRefresh();
  const [devices, setDevices] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [organizationId, setOrganizationId] = useState(() => sessionStorage.getItem("thrm_dashboard_org") || "all");
  const [measureAllBusy, setMeasureAllBusy] = useState(false);
  const [measureAllResult, setMeasureAllResult] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/devices").then((response) => {
      if (!Array.isArray(response.data)) throw new Error('Invalid device list');
      if (active) { setDevices(response.data); setLoadError(false); }
    }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [tick]);
  useEffect(() => {
    let active = true;
    setOrganizationsLoaded(false);
    if (isAdmin(user)) api.get("/organizations").then((response) => {
      if (!Array.isArray(response.data)) throw new Error('Invalid organization list');
      if (active) { setOrganizations(response.data.filter((org) => org.devices_count > 0)); setOrganizationsLoaded(true); }
    }).catch(() => {});
    return () => { active = false; };
  }, [user]);
  useEffect(() => {
    if (user) setOrganizationId(selected => resolveOrganizationFilter(selected, organizations, organizationsLoaded && devices !== null, isAdmin(user)));
  }, [user, organizations, organizationsLoaded, devices]);
  useEffect(() => { sessionStorage.setItem("thrm_dashboard_org", organizationId); }, [organizationId]);

  const filtered = useMemo(() => {
    if (!devices) return [];
    let result = devices.filter((device) =>
      `${device.name} ${device.location || ""} ${device.description || ""}`.toLowerCase().includes(query.toLowerCase())
    );
    if (organizationId !== "all") result = result.filter((device) => device.organization_id === organizationId);
    if (statusFilter !== "all") result = result.filter((device) => device.status_color === statusFilter || device.status_badge_color === statusFilter);
    result = [...result].sort((left, right) => {
      if (sort === "name") {
        const priority = (DEVICE_ORDER[left.external_device_id] ?? 2) - (DEVICE_ORDER[right.external_device_id] ?? 2);
        return priority || left.name.localeCompare(right.name);
      }
      if (sort === "value") return (right.last_value ?? -1) - (left.last_value ?? -1);
      if (sort === "status") {
        const order = { red: 0, orange: 1, blue: 2, green: 3, grey: 4 };
        return order[left.status_badge_color || left.status_color] - order[right.status_badge_color || right.status_color];
      }
      return 0;
    });
    return result;
  }, [devices, query, statusFilter, sort, organizationId]);

  const counts = useMemo(() => {
    const result = { total: 0, online: 0, offline: 0, attention: 0, critical: 0 };
    (devices || []).filter((device) => organizationId === "all" || device.organization_id === organizationId).forEach((device) => {
      result.total++;
      if (device.online) result.online++; else result.offline++;
      if (device.status_color === "orange") result.attention++;
      if (device.status_badge_color === "red" || device.status_color === "red") result.critical++;
    });
    return result;
  }, [devices, organizationId]);

  const activeDeviceCount = (devices || []).filter((device) => device.device_type !== "pressure_monitor" && canOperateDevice(user, device) && device.is_active && (organizationId === "all" || device.organization_id === organizationId)).length;
  const measureAll = async () => {
    setMeasureAllBusy(true);
    try {
      const response = await api.post("/devices/measure-all", { organization_id: organizationId === "all" ? null : organizationId });
      setMeasureAllResult(response.data);
      if (response.data.queued_count > 0) toast.success(t(`${response.data.queued_count} meetopdracht${response.data.queued_count === 1 ? "" : "en"} klaargezet.`, `${response.data.queued_count} measurement command${response.data.queued_count === 1 ? "" : "s"} queued.`));
      if (response.data.skipped_count > 0) toast.info(t(`${response.data.skipped_count} apparaat${response.data.skipped_count === 1 ? " is" : "en zijn"} overgeslagen omdat er al een opdracht klaarstaat.`, `${response.data.skipped_count} device${response.data.skipped_count === 1 ? " was" : "s were"} skipped because a command was already queued.`));
      refreshNow();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("De meetopdrachten konden niet worden klaargezet.", "The measurement commands could not be queued."));
    } finally {
      setMeasureAllBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("De actuele meetwaarden en verbinding per installatie.", "Current measurements and connection for each installation.")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {devices && <p className="text-xs font-medium text-muted-foreground">{counts.total} {counts.total === 1 ? t("installatie", "installation") : t("installaties", "installations")}</p>}
          <ValueGuideButton />
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/tv")}><Monitor className="h-4 w-4" />{t("Tv-overzicht", "TV overview")}</Button>
          {canMeasure(user) && !user?.preview_read_only && !isDemo(user) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" className="gap-2" disabled={!activeDeviceCount || measureAllBusy} data-testid="measure-all-devices">
                  <Radio className="h-4 w-4" />{measureAllBusy ? t("Meetronde voorbereiden…", "Preparing measurement round…") : t("Centrale meetronde starten", "Start central measurement round")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("Centrale meetronde starten?", "Start a central measurement round?")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {activeDeviceCount === 1
                      ? t("Start een nieuwe meetcyclus op het actieve apparaat. Het apparaat begint bij de volgende synchronisatie of zodra de verbinding terug is.", "Start a new measurement cycle on the active device. It starts at the next sync or when its connection returns.")
                      : t(`Start een nieuwe meetcyclus op alle ${activeDeviceCount} actieve apparaten. Online apparaten beginnen bij hun volgende synchronisatie; offline apparaten zodra hun verbinding terug is.`, `Start a new measurement cycle on all ${activeDeviceCount} active devices. Online devices start at their next sync; offline devices when their connection returns.`)}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Annuleren", "Cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={measureAll} data-testid="confirm-measure-all">{t("Meetronde starten", "Start measurement round")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {measureAllResult && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-700 dark:text-emerald-400" role="status" data-testid="measure-all-result">
          <span className="font-semibold">{t(`${measureAllResult.queued_count} meetopdracht${measureAllResult.queued_count === 1 ? "" : "en"} klaargezet.`, `${measureAllResult.queued_count} measurement command${measureAllResult.queued_count === 1 ? "" : "s"} queued.`)}</span>
          {measureAllResult.skipped_count > 0 && <span className="text-xs">{t(`${measureAllResult.skipped_count} overgeslagen: er stond al een opdracht klaar.`, `${measureAllResult.skipped_count} skipped: a command was already queued.`)}</span>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border shadow-sm lg:grid-cols-4">
        <SummaryItem label={t("Online", "Online")} value={devices === null ? '—' : counts.online} accent="text-emerald-500" />
        <SummaryItem label={t("Zonder verbinding", "No connection")} value={devices === null ? '—' : counts.offline} accent="text-slate-500" />
        <SummaryItem label={t("Aandacht nodig", "Needs attention")} value={devices === null ? '—' : counts.attention} accent="text-amber-500" />
        <SummaryItem label={t("Storingen", "Faults")} value={devices === null ? '—' : counts.critical} accent="text-red-500" />
      </div>

      {counts.critical > 0 && (
        <div className="flex items-center gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500" data-testid="critical-banner">
          <AlertTriangle className="h-4 w-4" /> {t(`${counts.critical} installatie(s) melden een storing en vragen om controle.`, `${counts.critical} installation(s) report a fault and require attention.`)}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("Zoek op naam of locatie…", "Search by name or location…")} className="pl-9" data-testid="device-search" />
        </div>
        {isAdmin(user) && <Select value={organizationId} onValueChange={setOrganizationId}><SelectTrigger className="w-full sm:w-60" data-testid="dashboard-organization-filter"><SelectValue placeholder={t("Alle organisaties", "All organizations")} /></SelectTrigger><SelectContent><SelectItem value="all">{t("Alle organisaties", "All organizations")}</SelectItem>{organizations.map((org) => <SelectItem key={org.id} value={org.id}>{org.name} ({org.devices_count})</SelectItem>)}</SelectContent></Select>}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Alle statussen", "All statuses")}</SelectItem>
            <SelectItem value="green">{t("In orde", "Healthy")}</SelectItem>
            <SelectItem value="orange">{t("Aandacht nodig", "Needs attention")}</SelectItem>
            <SelectItem value="blue">{t("Filteren actief", "Filtering active")}</SelectItem>
            <SelectItem value="red">{t("Storing", "Fault")}</SelectItem>
            <SelectItem value="grey">{t("Geen recente verbinding", "No recent connection")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full sm:w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">{t("Sorteer op naam", "Sort by name")}</SelectItem>
            <SelectItem value="value">{t("Sorteer op ISO", "Sort by ISO")}</SelectItem>
            <SelectItem value="status">{t("Sorteer op status", "Sort by status")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loadError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/35 bg-amber-500/10 p-4 text-sm"><span>{devices === null ? t("Installaties konden niet worden geladen.", "Installations could not be loaded.") : t("Vernieuwen mislukt. De laatst geladen installaties blijven zichtbaar.", "Refresh failed. The last loaded installations remain visible.")}</span><Button variant="outline" size="sm" onClick={refreshNow}>{t("Opnieuw proberen", "Retry")}</Button></div>}
      {devices === null && loadError ? null : devices === null ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-[285px] rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <Activity className="h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">{t("Geen installaties gevonden met deze filters.", "No installations match these filters.")}</p>
          <Button variant="outline" className="mt-4" onClick={() => { setOrganizationId('all'); setStatusFilter('all'); setQuery(''); }}>{t("Filters wissen", "Clear filters")}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filtered.map((device) => <TypedDeviceCard key={device.id} device={device} user={user} onMeasured={refreshNow} onClick={() => navigate(`/devices/${device.id}`)} />)}
        </div>
      )}
    </div>
  );
}
